"use client";

import { FileIcon, PlusIcon } from "@/components/icons";
import { SidebarHistory } from "@/components/sidebar-history";
import { Button } from "@/components/ui/button";
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    useSidebar,
} from "@/components/ui/sidebar";
import { useMemoryDialog } from "@/contexts/memory-dialog-context";
import type { User } from "next-auth";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";

import { SidebarToggle } from "./sidebar-toggle";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

export function AppSidebar({ user }: { user: User | undefined }) {
    const router = useRouter();
    const pathname = usePathname();
    const { setOpenMobile } = useSidebar();
    const { openMemoryDialog } = useMemoryDialog();

    // Only show sidebar on chat routes
    const isChatRoute =
        pathname === "/intelligence/chat" ||
        pathname?.startsWith("/intelligence/chat/");

    if (!isChatRoute) {
        return null;
    }

    return (
        <Sidebar className="group-data-[side=left]:border-r-0">
            <SidebarHeader className="px-3 py-2">
                <SidebarMenu>
                    <div className="hidden md:flex h-12" />
                    <div className="md:hidden flex flex-row justify-between items-center gap-2">
                        <SidebarToggle />
                        <Link
                            href="/intelligence/chat"
                            onClick={() => {
                                setOpenMobile(false);
                            }}
                            className="flex-1 overflow-hidden whitespace-nowrap"
                        >
                            <span className="text-lg font-semibold hover:bg-muted rounded-md cursor-pointer px-1 -ml-1">
                                Chatbot
                            </span>
                        </Link>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon" // Changed size
                                    type="button"
                                    className="h-8 w-8" // Set fixed size
                                    onClick={() => {
                                        setOpenMobile(false);
                                        router.push("/intelligence/chat");
                                        router.refresh();
                                    }}
                                >
                                    <PlusIcon size={18} />{" "}
                                    {/* Adjusted icon size */}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" align="end">
                                New Chat
                            </TooltipContent>
                        </Tooltip>
                    </div>
                </SidebarMenu>
            </SidebarHeader>
            <SidebarContent>
                <SidebarHistory user={user} />
            </SidebarContent>
            <SidebarFooter className="p-3 space-y-2">
                <Button
                    variant="outline"
                    type="button"
                    className="w-full"
                    onClick={() => {
                        openMemoryDialog();
                    }}
                >
                    <div className="flex flex-row gap-3 items-center">
                        <FileIcon size={18} />
                        Memory Bank
                    </div>
                </Button>
            </SidebarFooter>
        </Sidebar>
    );
}
